/**
 * VERIFIED_HARVEST_CORRECTION / POSSIBLE_HARVEST_CORRECTION signal
 * ──────────────────────────────────────────────────────────────────────────
 * Detects situations where historical routine sample weights were likely
 * underestimating the true average fish weight, based on later harvest-derived
 * measurements.
 *
 *   - VERIFIED_HARVEST_CORRECTION  (High confidence)
 *       Activates when a harvest-origin sample produces a jump that meets the
 *       growth-multiplier AND model-deviation thresholds AND is confirmed by
 *       at least 2 subsequent harvest-origin samples staying within ±15% of
 *       the new trajectory anchored at the trigger.
 *
 *   - POSSIBLE_HARVEST_CORRECTION  (Moderate confidence)
 *       Same activation thresholds but with 0 or 1 confirming harvest samples
 *       (cycle ended early, only one harvest exists, etc.). Wingman should
 *       recommend additional harvest-derived measurements before treating the
 *       interpretation as definitive.
 *
 * Returns:
 *   { status: 'none' | 'possible' | 'verified',
 *     trigger: { date, prior_weight_g, new_weight_g, expected_weight_g,
 *                expected_growth_g, observed_growth_g, multiplier,
 *                percent_above_model, interval_days } | null,
 *     confirmations: [{ date, weight_g, expected_g, pct_deviation }, ...],
 *     suggested_text: string,
 *     wingman_text: string }
 */

const SIGNIFICANT_GROWTH_MULTIPLIER = 2.0;   // observed ≥ 2× expected
const SIGNIFICANT_PCT_ABOVE_MODEL = 20.0;    // observed ≥ +20% over model
const MIN_CONFIRMATIONS_VERIFIED = 2;
const CONFIRMATION_TOLERANCE_PCT = 15.0;     // ±15% of new trajectory

const SUGGESTED_TEXT_VERIFIED =
  'Growth trajectory analysis identified a significant upward correction ' +
  'originating from a harvest-derived sample. The higher weight trajectory ' +
  'was subsequently supported by additional harvest data. This pattern is ' +
  'more consistent with historical sample underestimation than with ' +
  'accelerated biological growth or stock loss. Earlier routine samples ' +
  'may have systematically understated true average fish weight due to ' +
  'sampling bias, camera-estimation limitations, population variability, ' +
  'or non-representative fish selection. Harvest-derived measurements are ' +
  'considered the highest-confidence weight source within the production ' +
  'cycle and therefore take precedence when interpreting long-term growth ' +
  'performance.';

const SUGGESTED_TEXT_POSSIBLE =
  'A harvest-derived weight correction was detected. The pattern suggests ' +
  'previous routine samples may have underestimated true average fish ' +
  'weight, however insufficient harvest confirmation is currently available. ' +
  'Additional harvest-derived measurements would be required to fully ' +
  'validate this interpretation.';

// Wingman-specific verbiage (matches the customer-facing PRD verbatim).
const WINGMAN_TEXT_VERIFIED =
  'Harvest-derived weights strongly indicate that previous routine samples ' +
  'underestimated true average fish weight. Subsequent harvest measurements ' +
  'confirm the higher growth trajectory. This pattern is consistent with a ' +
  'sampling-quality issue rather than a missing-fish event.';

const WINGMAN_TEXT_POSSIBLE =
  'A harvest-derived weight correction was detected. The pattern suggests ' +
  'previous routine samples may have underestimated true average fish ' +
  'weight, however insufficient harvest confirmation is currently available. ' +
  'Additional harvest-derived measurements would be required to fully ' +
  'validate this interpretation.';

function daysBetween(a, b) {
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.round(ms / 86400000);
}

function modelWeightAt(dailyRows, isoDate) {
  if (!Array.isArray(dailyRows) || !isoDate) return null;
  const hit = dailyRows.find((r) => r && r.date === isoDate);
  if (!hit) return null;
  const w = hit.model_weight_g;
  return typeof w === 'number' && isFinite(w) && w > 0 ? w : null;
}

export function detectVerifiedHarvestCorrection({ samplePoints, dailyRows } = {}) {
  const empty = {
    status: 'none',
    trigger: null,
    confirmations: [],
    suggested_text: '',
    wingman_text: '',
  };

  if (!Array.isArray(samplePoints) || samplePoints.length < 2) return empty;
  if (!Array.isArray(dailyRows) || dailyRows.length === 0) return empty;

  // Sort by date ascending, defensively.
  const pts = samplePoints
    .filter((p) => p && p.date && typeof p.weight === 'number' && p.weight > 0 && (p.type === 'sample' || p.type === 'harvest'))
    .slice()
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  // Best candidate: prefer the FIRST trigger that produces 'verified'.
  // If none reach 'verified', fall back to the FIRST trigger that meets the
  // jump thresholds (POSSIBLE_HARVEST_CORRECTION).
  let possibleHit = null;

  for (let i = 1; i < pts.length; i++) {
    const trigger = pts[i];
    if (trigger.type !== 'harvest') continue;

    const prior = pts[i - 1];
    const observedGrowthG = trigger.weight - prior.weight;
    if (observedGrowthG <= 0) continue;

    const priorModelW = modelWeightAt(dailyRows, prior.date);
    const triggerModelW = modelWeightAt(dailyRows, trigger.date);
    if (priorModelW == null || triggerModelW == null) continue;

    const expectedGrowthG = triggerModelW - priorModelW;
    if (expectedGrowthG <= 0) continue;

    const multiplier = observedGrowthG / expectedGrowthG;
    const pctAboveModel = ((trigger.weight - triggerModelW) / triggerModelW) * 100;

    if (multiplier < SIGNIFICANT_GROWTH_MULTIPLIER) continue;
    if (pctAboveModel < SIGNIFICANT_PCT_ABOVE_MODEL) continue;

    // Confirmation pass: subsequent HARVEST points only.
    const subsequentHarvests = pts.slice(i + 1).filter((p) => p.type === 'harvest');
    const confirmations = [];
    let contradicted = false;
    for (const h of subsequentHarvests) {
      const hModelW = modelWeightAt(dailyRows, h.date);
      if (hModelW == null) continue;
      // Expected on the new trajectory = trigger_weight × (model_at_h / model_at_trigger)
      const expectedOnNew = trigger.weight * (hModelW / triggerModelW);
      const pctDev = ((h.weight - expectedOnNew) / expectedOnNew) * 100;
      if (Math.abs(pctDev) <= CONFIRMATION_TOLERANCE_PCT) {
        confirmations.push({
          date: h.date,
          weight_g: h.weight,
          expected_g: expectedOnNew,
          pct_deviation: pctDev,
        });
      } else {
        contradicted = true;
        break;
      }
    }
    if (contradicted) continue;

    const triggerSummary = {
      date: trigger.date,
      prior_date: prior.date,
      prior_weight_g: prior.weight,
      new_weight_g: trigger.weight,
      expected_weight_g: triggerModelW,
      expected_growth_g: expectedGrowthG,
      observed_growth_g: observedGrowthG,
      multiplier,
      percent_above_model: pctAboveModel,
      interval_days: daysBetween(prior.date, trigger.date),
    };

    if (confirmations.length >= MIN_CONFIRMATIONS_VERIFIED) {
      // VERIFIED — return immediately (highest priority).
      return {
        status: 'verified',
        trigger: triggerSummary,
        confirmations,
        suggested_text: SUGGESTED_TEXT_VERIFIED,
        wingman_text: WINGMAN_TEXT_VERIFIED,
      };
    }

    // Hold the first POSSIBLE candidate in case no later trigger verifies.
    if (!possibleHit) {
      possibleHit = {
        status: 'possible',
        trigger: triggerSummary,
        confirmations,
        suggested_text: SUGGESTED_TEXT_POSSIBLE,
        wingman_text: WINGMAN_TEXT_POSSIBLE,
      };
    }
  }

  return possibleHit || empty;
}

// Exposed for testing
export const __thresholds = {
  SIGNIFICANT_GROWTH_MULTIPLIER,
  SIGNIFICANT_PCT_ABOVE_MODEL,
  MIN_CONFIRMATIONS_VERIFIED,
  CONFIRMATION_TOLERANCE_PCT,
};
